import {debounce} from 'lodash-es';
import {useCallback, useEffect} from 'react';

import {SimpleTextField} from '../../_old/TextField/SimpleTextField';

import {isObjectSearchParam} from './common';
import {SearchContainer} from './SearchComponents';

import type {
  SearchBarCoreProps2,
  SearchParam as InternalSearchParam,
} from './common';

const debouncedResultsHandler = debounce(
  <Searchable extends {id: string}>(
    searchValue: string,
    params: Props<Searchable>['params'],
    collection: Props<Searchable>['collection'],
    handleSearchResults: Props<Searchable>['handleSearchResults']
  ) => {
    const r = collection.filter((item) => {
      return params.some((param) => {
        if (isObjectSearchParam(param)) {
          return param.compare(searchValue, item[param.name]);
        } else {
          if (typeof item[param] === 'string') {
            const p = (item[param] as string).toLowerCase().trim();
            return p && searchValue && p.includes(searchValue.toLowerCase());
          }

          return false;
        }
      });
    });

    handleSearchResults(r, searchValue);
  },
  300
);

type Props<T extends {id: string}> = SearchBarCoreProps2<T> & {
  handleSearchResults: (v: T[], search: string) => void;
  searchValue: string;
  setSearchValue: (value: string) => void;
  shouldClearOnBlur?: boolean;
};
export function SearchBar2<Searchable extends {id: string}>(
  props: Props<Searchable>
): React.JSX.Element {
  const {collection, params, handleSearchResults, setSearchValue} = props;

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>): void => {
      const v = e.target.value;
      setSearchValue(v);
      debouncedResultsHandler(
        v,
        // @ts-expect-error don't know proper type, works fine
        params,
        collection,
        handleSearchResults
      );
    },
    [collection, handleSearchResults, params, setSearchValue]
  );

  useEffect(() => {
    return () => {
      debouncedResultsHandler.cancel();
    };
  }, []);

  return (
    <SearchContainer
      className={props.className}
      onBlur={() => {
        if (props.shouldClearOnBlur) {
          props.setSearchValue('');
          handleSearchResults([], '');
        }
      }}
    >
      <SimpleTextField
        value={props.searchValue}
        aria-autocomplete="list"
        autoComplete="off"
        onChange={handleChange}
        {...props.inputProps}
      >
        {props.label}
      </SimpleTextField>

      {props.children}
    </SearchContainer>
  );
}

export type SearchParam2<T> = InternalSearchParam<T>;
